import React, { useState, useEffect } from 'react';
import KickImage from 'Reuseables/KickImage';
import ReactPlaceholder from 'react-placeholder';
import { TextBlock, RectShape } from 'react-placeholder/lib/placeholders';
import { Link } from 'react-router-dom';

/** CSS */
import Styles from './UpcomingReleases.module.scss';
import 'react-placeholder/lib/reactPlaceholder.css';

/** Hooks */
import useUpcomingRelease from './useUpcomingRelease';
import { useGetViewType } from 'Pages/Home/Hooks/useGetViewType';

/** Utils */
import { convertBrandNameToSlug } from 'utils/Helpers';
import { emptyImage, getCloudImageUrl } from 'utils';

/** Components */
import Card from './Components/Card';
import Header from './Components/Header';
import Arrow from './Components/Arrow';

const UpcomingReleases: React.FC = () => {
  const { data, loading, getUpcomingRelease } = useUpcomingRelease();
  const [category, setCategory] = useState('sneakers');
  const { isMobileView } = useGetViewType();
  const itemScroll = React.useRef<HTMLDivElement>(null);
  const [scrollLeftValue, setScrollLeftValue] = React.useState<number>(0);
  const [hovered, setHovered] = useState(false);

  const Placeholder = React.useMemo(
    () => (
      <div>
        <div className={Styles['placeholder']}>
          {Array.from({ length: 5 }).map((_, index) => (
            <div className={Styles['placeholder-item']}>
              <RectShape
                color="#eeeeee"
                style={{
                  width: '100%',
                  height: 154,
                  margin: 0,
                  marginBottom: 12,
                }}
              />
              <TextBlock rows={2} color="#eeeeee" />
            </div>
          ))}
        </div>
      </div>
    ),
    [],
  );

  const formulateLink = (item) => {
    const brand = item?.product?.brand ?? '';
    const brandSlug = brand?.flat?.[0]
      ? convertBrandNameToSlug(brand?.flat?.[0] ?? '')
      : brand?.slug;
    const slug = item?.slug ?? '';
    let categoryForUrl = item?.product?.category?.name ?? '';

    switch (categoryForUrl) {
      case 'apparels':
      case 'Apparels': {
        categoryForUrl = 'fashion';
        break;
      }
      case 'handbags': {
        categoryForUrl = 'luxury';
        break;
      }
      default:
        break;
    }

    return `${categoryForUrl}/${brandSlug}/${slug}`;
  };

  const onScrollItems = () => {
    if (!itemScroll.current) {
      return;
    }

    const {
      current: { scrollWidth, clientWidth, scrollLeft },
    } = itemScroll;

    const scrollLeftCeil = Math.ceil(scrollLeft);
    const maxScrollWidth = scrollWidth - clientWidth;

    if (scrollLeftCeil % 100 === 0) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeftCeil >= maxScrollWidth) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeft === 0) {
      setScrollLeftValue(0);
    }
  };

  const isShowingArrow = (arrow: 'left' | 'right'): boolean => {
    if (!itemScroll.current) {
      return false;
    }

    if (itemScroll.current.scrollWidth <= itemScroll.current.clientWidth) {
      return false;
    }

    if (isMobileView) {
      return false;
    }

    if (!hovered) {
      return false;
    }

    if (arrow === 'left') {
      return scrollLeftValue > 0;
    }

    if (arrow === 'right') {
      const maxScrollWidth =
        itemScroll.current.scrollWidth - itemScroll.current.clientWidth;
      return scrollLeftValue < maxScrollWidth;
    }

    return false;
  };

  const rightArrowClicked = () => {
    if (!itemScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(itemScroll.current.scrollLeft);
    const maxScrollWidth =
      itemScroll.current.scrollWidth - itemScroll.current.clientWidth;

    if (scrolledNow >= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!itemScroll.current) {
        return;
      }

      itemScroll.current.scrollLeft += isMobileView ? 2 : 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!itemScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(itemScroll.current.scrollLeft));
    }, 1000);
  };

  const leftArrowClicked = () => {
    if (!itemScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(itemScroll.current.scrollLeft);
    const maxScrollWidth = 0;

    if (scrolledNow <= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!itemScroll.current) {
        return;
      }

      itemScroll.current.scrollLeft -= isMobileView ? 2 : 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!itemScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(itemScroll.current.scrollLeft));
    }, 1000);
  };

  const getImageUrl = (item) => {
    const url =
      item?.imageUrl ?? item?.product_variant_images?.[0]?.URL ?? '';
    const category = item?.product?.category?.name ?? '';

    return url ? getCloudImageUrl(url, 400) : emptyImage(category);
  };

  useEffect(() => {
    setScrollLeftValue(0);
    getUpcomingRelease(category);
  }, [category]);

  return (
    <div id={Styles['news-upcoming-release']}>
      {isMobileView && (
        <Header category={category} setCategory={(cat) => setCategory(cat)} />
      )}

      <div className={Styles['nur-cards']}>
        {!isMobileView && (
          <Header category={category} setCategory={(cat) => setCategory(cat)} />
        )}

        {!loading && !data?.length ? (
          <div className={Styles['empty-ui']}>
            <p>No products available</p>
          </div>
        ) : (
          <ReactPlaceholder ready={!loading} customPlaceholder={Placeholder}>
            <div
              onMouseLeave={() => setHovered(false)}
              onMouseEnter={() => setHovered(true)}
              className={Styles['hovered-wrapper']}
            >
              <Arrow
                type="left"
                onClick={leftArrowClicked}
                show={isShowingArrow('left')}
              />

              <div
                className={Styles['item-wrapper']}
                ref={itemScroll}
                onScroll={onScrollItems}
              >
                {data.length
                  ? data.map((datum: any, key) => {
                      return (
                        <div>
                          <Card
                            link={formulateLink(datum)}
                            name={datum?.display_name ?? ''}
                            productImg={getImageUrl(datum)}
                            date={datum?.date ?? ''}
                            category={datum?.product?.category?.name ?? ''}
                          />
                        </div>
                      );
                    })
                  : null}
                <div>
                  <Link to="/" className={Styles['ending-card']}>
                    <p>Visit</p>
                    <p>Marketplace</p>
                  </Link>
                </div>
              </div>

              <Arrow
                type="right"
                onClick={rightArrowClicked}
                show={isShowingArrow('right')}
              />
            </div>
          </ReactPlaceholder>
        )}
      </div>
    </div>
  );
};

export default UpcomingReleases;
